/* eslint-disable no-param-reassign */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useTranslator } from '@jutro/locale';
import { QuestionSetsParser } from 'ml-portals-questionsets-js';
import { QuestionSetUtils } from '@xengage/gw-portals-util-js';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import messages from './QuestionSetComponent.messages';

function QuestionSetComponent(props) {
    const translator = useTranslator();
    const {
        id,
        onValidate,
        onValueChange,
        onBlur,
        value: aQuestionSetAnswersValue,
        path: pathToAnswers,
        labelPosition,
        title,
        questionsToShowML,
        showHeaderML,
        showCompRaterSpacing,
        showErrors,
        showOptionalML,
        isFieldDefaultedByHVQ,
        currentStep,
        checkReadOnlyAnswer,
        filterChoicesLists
    } = props;
    const {
        isComponentValid,
        onValidate: setComponentValidation,
        registerComponentValidation
    } = useValidation(id);
    const [questionSetsMetadata, setQuestionSetsMetadata] = useState(null);

    useEffect(() => {
        import(
            /* webpackChunkName: "question-sets-metadata" */
            // eslint-disable-next-line import/no-unresolved
            'question-sets-metadata'
        ).then((data) => {
            const { default: result } = data;
            setQuestionSetsMetadata(result);
        });
    }, []);

    const checkComponentValidation = useCallback(() => {
        return questionSetsMetadata ? true : null;
    }, [questionSetsMetadata]);

    useEffect(() => {
        registerComponentValidation(checkComponentValidation);
    }, [checkComponentValidation, registerComponentValidation]);

    useEffect(() => {
        if (onValidate) {
            onValidate(isComponentValid, id);
        }
    }, [id, onValidate, isComponentValid]);

    const handleValueChange = useCallback(
        (value, path) => {
            const { code: questionSetCode, answers: questionSetAnswer } = aQuestionSetAnswersValue;
            const qsOrderedQuestions = questionSetsMetadata[questionSetCode].orderedQuestions;
            // aspects.DataType
            const qsQuestionType = _.find(qsOrderedQuestions, { code: path }).questionType;

            if (value === undefined) {
                if (onValueChange) {
                    onValueChange(aQuestionSetAnswersValue, pathToAnswers || `answers.${path}`, qsQuestionType);
                }
                return;
            }

            QuestionSetUtils.cleanDependantQuestions(
                qsOrderedQuestions,
                value,
                path,
                questionSetAnswer
            );

            let val = value;

            if (value.code) {
                val = value.code;
            }
            if (val.length === 0) {
                val = null;
            }
            if (val !== null && val.year !== undefined && val.month !== undefined && val.day !== undefined) {
                val = `${val.year}-${val.month}-${val.day}`;
            }
            _.set(aQuestionSetAnswersValue, `answers.${path}`, val);

            if (onValueChange) {
                onValueChange(aQuestionSetAnswersValue, pathToAnswers || `answers.${path}`, qsQuestionType);
            }
        },
        [aQuestionSetAnswersValue, onValueChange, pathToAnswers, questionSetsMetadata]
    );

    const handleBlurChange = useCallback(
        (event, questionCode, questionType) => {
            if (onBlur) {
                onBlur(
                    aQuestionSetAnswersValue,
                    pathToAnswers || `answers.${questionCode}`,
                    questionType,
                    event
                );
            }
        },
        [aQuestionSetAnswersValue, onBlur, pathToAnswers]
    );

    if (!questionSetsMetadata) {
        return null;
    }

    const { code: questionSetCode } = aQuestionSetAnswersValue;
    const qsOrderedQuestions = questionSetsMetadata[questionSetCode].orderedQuestions;

    const questionBlurOverrides = qsOrderedQuestions.reduce(
        (overrides, question) => {
            overrides[question.code] = {
                onBlur: (event) => handleBlurChange(
                    event,
                    question.code,
                    question.questionType
                )
            };

            return overrides;
        },
        {}
    );

    const overrideProps = {
        '@field': {
            showOptional: showOptionalML,
            labelPosition: labelPosition
        },
        ...questionBlurOverrides
    };

    const { viewModel: qsViewModel, presentationMetadata: qsMetadata } = new QuestionSetsParser(
        aQuestionSetAnswersValue,
        questionSetsMetadata,
        translator,
        questionsToShowML,
        showCompRaterSpacing,
        isFieldDefaultedByHVQ,
        currentStep,
        checkReadOnlyAnswer,
        filterChoicesLists
    );

    return (
        <div>
            {showHeaderML
                && (
                    <h2 id={`${id}_questionSetHeader`} className="wizardTitle">
                        {translator(title)}
                    </h2>
                )
            }
            <ViewModelForm
                uiProps={qsMetadata}
                model={qsViewModel}
                overrideProps={overrideProps}
                onValueChange={handleValueChange}
                onValidationChange={setComponentValidation}
                showErrors={showErrors}
            />
        </div>
    );
}

/**
 * @memberof gw-components-platform-react.QuestionSetComponent
 * @property {Object} propTypes.value - value of the property being rendered
 * @property {string} propTypes.path - the path where this component is attached
 * @property {function(newAnswers, pathToAnswers)} propTypes.onValueChange - the
 *                                      function invoked when the value is changed
 */
QuestionSetComponent.propTypes = {
    value: PropTypes.shape({
        code: PropTypes.string
    }).isRequired,
    path: PropTypes.string,
    onValueChange: PropTypes.func,
    onBlur: PropTypes.func,
    onValidate: PropTypes.func,
    labelPosition: PropTypes.string,
    id: PropTypes.string,
    title: PropTypes.shape({
        id: PropTypes.string,
        defaultMessage: PropTypes.string
    }),
    questionsToShowML: PropTypes.arrayOf(PropTypes.string),
    showHeaderML: PropTypes.bool,
    showCompRaterSpacing: PropTypes.bool,
    showErrors: PropTypes.bool,
    showOptionalML: PropTypes.bool,
    isFieldDefaultedByHVQ: PropTypes.func,
    filterChoicesLists: PropTypes.func
};
QuestionSetComponent.defaultProps = {
    path: undefined,
    onValueChange: undefined,
    onBlur: undefined,
    onValidate: undefined,
    labelPosition: 'left',
    id: undefined,
    title: messages.pageTitle,
    questionsToShowML: undefined,
    showHeaderML: true,
    showCompRaterSpacing: false,
    showErrors: false,
    showOptionalML: false,
    isFieldDefaultedByHVQ: undefined,
    filterChoicesLists: undefined
};
export default QuestionSetComponent;